import { useState, useCallback, useRef } from "react"
import Logger from "classes/Logger"
import { request_post } from "services/OSRM"
import useCancelToken from "hooks/UseCancelToken"
import { IMenu } from "interfaces/IMenu"
import { TransactionCodeEnum } from "enums/TransactionCodeEnum"
import useLogin from "hooks/UseLogin"

interface UseUpdateMenuCountsReturn {
  menuItems: IMenu[]
  loading: boolean
  updateCounts: (items: IMenu[]) => Promise<void>
}

/**
 * Hook para actualizar los conteos (optionCount) de los items del menú
 * haciendo llamados paralelos al servicio de búsqueda de shipping groups
 */
const useUpdateMenuCounts = (): UseUpdateMenuCountsReturn => {
  const { newCancelToken, isCancel } = useCancelToken()
  const { logout } = useLogin()
  const [menuItems, setMenuItems] = useState<IMenu[]>([])
  const [loading, setLoading] = useState<boolean>(false)
  const isProcessingRef = useRef<boolean>(false)

  // Función helper para manejar errores 401
  const handleUnauthorizedError = useCallback((error: any) => {
    if (error.response?.status === TransactionCodeEnum.unauthorized) {
      Logger.error('Error 401 Unauthorized detectado:', error)
      logout()
      return true
    }
    return false
  }, [logout])

  /**
   * Actualiza los conteos de los items del menú haciendo llamados paralelos
   * al servicio de búsqueda de shipping groups
   * @param items - Array de items del menú con body configurado
   */
  const updateCounts = useCallback(async (items: IMenu[]) => {
    if (!items || items.length === 0) {
      setMenuItems(items)
      return
    }

    // Evitar ejecuciones simultáneas
    if (isProcessingRef.current) {
      Logger.info('Ya hay una actualización de conteos en progreso, omitiendo llamada')
      return
    }

    try {
      isProcessingRef.current = true
      setLoading(true)
      
      // Hacer llamados paralelos para obtener los conteos
      const updatedItems = await Promise.all(
        items.map(async (item) => {
          // Si no tiene body, mantener el conteo en 0
          if (!item.body) {
            return { ...item, optionCount: 0 }
          }

          try {
            const response = await request_post('search/shipping_groups', item.body, newCancelToken())
            const total = response.data?.message?.total || 0
            
            return {
              ...item,
              optionCount: total
            }
          } catch (error: any) {
            Logger.error(`Error al obtener conteo para ${item.title}:`, error)
            
            if (isCancel(error)) {
              return item
            }
            
            if (handleUnauthorizedError(error)) {
              return item
            }
            
            // En caso de error, mantener el conteo en 0
            return {
              ...item,
              optionCount: 0
            }
          }
        })
      )
      
      setMenuItems(updatedItems)
    } catch (error: any) {
      Logger.error('Error al actualizar contadores del menú:', error)
      
      if (isCancel(error)) {
        return
      }
      
      if (handleUnauthorizedError(error)) {
        return
      }
      
      // En caso de error general, mantener los items originales
      setMenuItems(items)
    } finally {
      setLoading(false)
      isProcessingRef.current = false
    }
  }, [newCancelToken, isCancel, handleUnauthorizedError])

  return {
    menuItems,
    loading,
    updateCounts
  }
}

export default useUpdateMenuCounts

